/**
 * Keyboard Navigation Utilities
 * 
 * Reusable utilities for implementing keyboard navigation patterns
 * across chess components. Based on existing patterns from PromotionDialog.tsx.
 */

export interface NavigationConfig {
  /** Whether to wrap around when reaching the end */
  wrap?: boolean;
  /** Custom key mappings */
  keyMappings?: {
    next?: string[];
    previous?: string[];
    select?: string[];
    cancel?: string[];
  };
}

export interface GridNavigationConfig extends NavigationConfig {
  /** Number of columns in the grid */
  columns: number;
  /** Number of rows in the grid */
  rows: number;
  /** Whether to allow diagonal navigation */
  allowDiagonal?: boolean;
}

/**
 * Default key mappings for navigation
 */
export const DEFAULT_KEY_MAPPINGS = {
  next: ['ArrowRight', 'ArrowDown'],
  previous: ['ArrowLeft', 'ArrowUp'],
  select: ['Enter', ' '],
  cancel: ['Escape']
};

/**
 * Find all focusable elements within a container
 * @param container The container element to search within
 * @returns Array of focusable elements
 */
export const findFocusableElements = (container: HTMLElement): HTMLElement[] => {
  const focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
    '[data-focusable="true"]'
  ].join(', ');

  return Array.from(container.querySelectorAll(focusableSelectors)) as HTMLElement[];
};

/**
 * Handle linear navigation (left/right, up/down)
 * @param event The keyboard event
 * @param elements Array of focusable elements
 * @param currentIndex Current focused element index
 * @param config Navigation configuration
 * @returns New index to focus, or null if no change
 */
export const handleLinearNavigation = (
  event: KeyboardEvent,
  elements: HTMLElement[],
  currentIndex: number,
  config: NavigationConfig = {}
): number | null => {
  const { wrap = true, keyMappings = DEFAULT_KEY_MAPPINGS } = config;
  
  if (!elements.length) return null;

  let newIndex = currentIndex;

  if (keyMappings.next?.includes(event.key)) {
    newIndex = currentIndex + 1;
    if (newIndex >= elements.length) {
      newIndex = wrap ? 0 : currentIndex; // Stay at current if no wrap
    }
  } else if (keyMappings.previous?.includes(event.key)) {
    newIndex = currentIndex - 1;
    if (newIndex < 0) {
      newIndex = wrap ? elements.length - 1 : currentIndex; // Stay at current if no wrap
    }
  } else {
    return null; // No navigation key pressed
  }

  return newIndex !== currentIndex ? newIndex : null;
};

/**
 * Handle grid-based navigation (chess board, button grids)
 * @param event The keyboard event
 * @param currentRow Current row (0-based)
 * @param currentCol Current column (0-based)
 * @param config Grid navigation configuration
 * @returns New position {row, col} or null if no change
 */
export const handleGridNavigation = (
  event: KeyboardEvent,
  currentRow: number,
  currentCol: number,
  config: GridNavigationConfig
): { row: number; col: number } | null => {
  const { rows, columns, wrap = true } = config;
  
  let newRow = currentRow;
  let newCol = currentCol;

  switch (event.key) {
    case 'ArrowUp':
      newRow = currentRow - 1;
      if (newRow < 0) {
        newRow = wrap ? rows - 1 : currentRow; // Stay at current if no wrap
      }
      break;
    case 'ArrowDown':
      newRow = currentRow + 1;
      if (newRow >= rows) {
        newRow = wrap ? 0 : currentRow; // Stay at current if no wrap
      }
      break;
    case 'ArrowLeft':
      newCol = currentCol - 1;
      if (newCol < 0) {
        newCol = wrap ? columns - 1 : currentCol; // Stay at current if no wrap
      }
      break;
    case 'ArrowRight':
      newCol = currentCol + 1;
      if (newCol >= columns) {
        newCol = wrap ? 0 : currentCol; // Stay at current if no wrap
      }
      break;
    default:
      return null; // No navigation key pressed
  }

  return (newRow !== currentRow || newCol !== currentCol) 
    ? { row: newRow, col: newCol } 
    : null;
};

/**
 * Focus an element and scroll it into view if needed
 * @param element The element to focus
 * @param scrollIntoView Whether to scroll the element into view
 */
export const focusElement = (element: HTMLElement, scrollIntoView = true): void => {
  element.focus();
  if (scrollIntoView) {
    element.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'nearest',
      inline: 'nearest'
    });
  }
};

/**
 * Cycle focus through a list of elements
 * @param elements Array of focusable elements
 * @param direction Direction to cycle ('next' or 'previous')
 * @param currentElement Currently focused element
 * @param wrap Whether to wrap around
 * @returns The newly focused element, or null if no change
 */
export const cycleFocus = (
  elements: HTMLElement[],
  direction: 'next' | 'previous',
  currentElement?: HTMLElement,
  wrap = true
): HTMLElement | null => {
  if (!elements.length) return null;

  const currentIndex = currentElement 
    ? elements.indexOf(currentElement)
    : -1;

  let newIndex: number;

  if (direction === 'next') {
    newIndex = currentIndex + 1;
    if (newIndex >= elements.length) {
      newIndex = wrap ? 0 : elements.length - 1;
    }
  } else {
    newIndex = currentIndex - 1;
    if (newIndex < 0) {
      newIndex = wrap ? elements.length - 1 : 0;
    }
  }

  const newElement = elements[newIndex];
  if (newElement && newElement !== currentElement) {
    focusElement(newElement);
    return newElement;
  }

  return null;
};

/**
 * Convert chess square notation to grid coordinates
 * @param square Chess square (e.g., 'e4')
 * @returns Grid coordinates {row, col} or null if invalid
 */
export const squareToGridCoords = (square: string): { row: number; col: number } | null => {
  if (square.length !== 2) return null;
  
  const file = square.charCodeAt(0) - 'a'.charCodeAt(0); // 0-7
  const rank = parseInt(square[1]) - 1; // 0-7
  
  if (file < 0 || file > 7 || rank < 0 || rank > 7) return null;
  
  // Convert to grid coordinates (row 0 = rank 8, col 0 = file a)
  return {
    row: 7 - rank,
    col: file
  };
};

/**
 * Convert grid coordinates to chess square notation
 * @param row Grid row (0-7, where 0 = rank 8)
 * @param col Grid column (0-7, where 0 = file a)
 * @returns Chess square notation or null if invalid
 */
export const gridCoordsToSquare = (row: number, col: number): string | null => {
  if (row < 0 || row > 7 || col < 0 || col > 7) return null;
  
  const file = String.fromCharCode('a'.charCodeAt(0) + col);
  const rank = (8 - row).toString();
  
  return file + rank;
};

/**
 * Handle keyboard shortcuts with preventDefault
 * @param event The keyboard event
 * @param shortcuts Map of key combinations to handler functions
 * @param preventDefault Whether to prevent default behavior
 */
export const handleKeyboardShortcuts = (
  event: KeyboardEvent,
  shortcuts: Record<string, () => void>,
  preventDefault = true
): boolean => {
  // Create key combination string
  const modifiers = [];
  if (event.ctrlKey) modifiers.push('Ctrl');
  if (event.altKey) modifiers.push('Alt');
  if (event.shiftKey) modifiers.push('Shift');
  if (event.metaKey) modifiers.push('Meta');
  
  const keyCombo = [...modifiers, event.key].join('+');
  const simpleKey = event.key;
  
  // Check for exact match first, then simple key
  const handler = shortcuts[keyCombo] || shortcuts[simpleKey];
  
  if (handler) {
    if (preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }
    handler();
    return true;
  }
  
  return false;
}; 